// External library imports
import { LANDING_PATH as FW_LANDING_PATH } from '../../../constants/frameworkAgreementSegments';
import {
  ListChecks,
  UsersThree,
  UserGear,
  Building,
  // FadersHorizontal,
  ListStar,
  MapPinArea,
  Certificate,
  CurrencyCircleDollar,
  Warehouse,
  Stack,
  SquareHalf,
  ChatText,
  Book as BookIcon,
  FolderStar,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Internal service imports
import { tokenManager } from '../../../services/tokenManager';

// Constants and configuration imports
import {
  IS_LOCAL_ENV,
  LOCAL_ADMIN_EMAIL,
} from '../../../constants/environment';

// GraphQL hooks imports
import { useEmployeeDetails } from '../../../graphql/hooks/employees/getEmployeeDetails';

import { isFeatureEnabled, FEATURES } from '../../../utils/featureFlags';

import WarningMessage from '../../atoms/WarningMessage/WarningMessage';

/**
 * AppIntro component serves as an introductory section for the application.
 * It provides users with a greeting based on the time of day and displays
 * navigation cards that link to various modules such as job coordination,
 * client management, and vendor organization. Each card includes an icon,
 * title, description, and a link to the respective module.
 *
 * @param {'default' | 'notFound'} [props.variant] - When `notFound`, shows page-not-found copy and the requested path.
 */

const AppIntro = ({ variant = 'default' }) => {
  // const location = useLocation();
  const isNotFound = variant === 'notFound';
  // Tag color mapping
  const tagColorMap = {
    'Client Management': 'bg-purple-100 text-purple-800', // `'bg-orange-100 text-orange-800',
    // 'Job Coordination': 'bg-purple-100 text-purple-800',
    'Data Management': 'bg-amber-100 text-amber-800',
    'Inspector Management': 'bg-blue-100 text-blue-800',
    'Vendor Management': 'bg-green-100 text-green-800',
  };

  // eslint-disable-next-line no-unused-vars
  const { isLoading } = useEmployeeDetails(
    IS_LOCAL_ENV ? LOCAL_ADMIN_EMAIL : tokenManager.getUserEmail()
  );
  // console.log(
  //   isLoading ? 'Employee details loading...' : 'Employee Details loaded'
  // );

  /**
   * getGreeting function returns a greeting based on the time of day.
   * It checks the current hour and returns the appropriate greeting.
   * @returns {string} - The greeting based on the time of day.
   */
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  /**
   * navigationCards is an array of objects that represent the navigation cards.
   * Each card includes an icon, title, description, link, isReleased, and linkLabel.
   * @type {Array}
   */
  const navigationCards = [
    isFeatureEnabled(FEATURES.CLIENTS) && {
      // Client Management
      icon: <UsersThree size={36} />,
      title: 'Organize Clients',
      tag: 'Client Management',
      description:
        'Manage clients and their inspection agreements. Track client relationships and projects.',
      link: '/clients',
      isReleased: true,
      linkLabel: 'Go to Clients Module',
    },

    isFeatureEnabled(FEATURES.INQUIRIES) && {
      // Inquiries Management
      icon: <ChatText size={36} />,
      title: 'Handle Inquiries',
      tag: 'Client Management',
      description:
        'Process and respond to client inquiries. Track communication and follow up on requests.',
      link: '/inquiries',
      isReleased: true,
      linkLabel: 'Go to Inquiries Module',
    },

    isFeatureEnabled(FEATURES.FRAMEWORK_AGREEMENTS) && {
      icon: <BookIcon size={36} />,
      title: 'Framework Agreements',
      tag: 'Client Management',
      description:
        'Track agreement terms, commercials, and related documents with clients.',
      // Land on the user's drafts (their own in-progress work) rather than the full list.
      link: FW_LANDING_PATH,
      isReleased: true,
      linkLabel: 'Go to FW Agreements Module',
    },

    isFeatureEnabled(FEATURES.WORK_ORDERS) && {
      // Work Orders Management
      icon: <SquareHalf size={36} />,
      title: 'Manage Work Orders',
      tag: 'Client Management',
      description:
        'Organize and manage work orders. Oversee job order assignments and specs.',
      link: '/work-orders',
      isReleased: true,
      linkLabel: 'Go to Work Orders',
    },

    isFeatureEnabled(FEATURES.JOBS) && {
      // Job Coordination
      icon: <ListChecks size={36} />,
      title: 'Coordinate Jobs',
      tag: 'Client Management',
      description:
        'Track inspection jobs. Monitor progress and manage job assignments across clients.',
      link: '/jobs',
      isReleased: true,
      linkLabel: 'Go to Jobs Module',
    },

    isFeatureEnabled(FEATURES.CLIENT_QMS_CERTIFICATES) && {
      icon: <FolderStar size={36} />,
      title: 'Client Certificates',
      tag: 'Client Management',
      description: 'Manage client certificates and related documentation.',
      link: '/client-certificates',
      isReleased: true,
      linkLabel: 'Go to Client Certificates',
    },

    isFeatureEnabled(FEATURES.INSPECTORS) && {
      // Inspector Management
      icon: <UserGear size={36} />,
      title: 'Oversee Inspectors',
      tag: 'Inspector Management',
      description:
        'Oversee inspector profiles and competencies. Track inspector performance and assignments.',
      link: '/inspectors',
      isReleased: true,
      linkLabel: 'Go to Inspectors Module',
    },

    isFeatureEnabled(FEATURES.AGENCIES) && {
      // Agency Management
      icon: <Building size={36} />,
      title: 'Overlook Agencies',
      tag: 'Inspector Management',
      description:
        'Work with inspector providers. Manage agency relationships and inspector sourcing.',
      link: '/agencies',
      isReleased: true,
      linkLabel: 'Go to Agencies Module',
    },

    isFeatureEnabled(FEATURES.INSPECTORS) && {
      // Inspector Skills Management
      icon: <ListStar size={36} />,
      title: 'Configure Inspector Skills',
      tag: 'Inspector Management',
      description:
        'Add, update, or archive skills for inspectors. Adjust related configurations.',
      link: '/inspectors/skills-management',
      isReleased: true,
      linkLabel: 'Go to Inspector Skills Module',
    },

    isFeatureEnabled(FEATURES.INSPECTORS) && {
      // Inspector Certificates Management
      icon: <Certificate size={36} />,
      title: 'Config. Insp. Certificates',
      tag: 'Inspector Management',
      description:
        'Add, update, or archive certificates for inspectors. Adjust related configurations.',
      link: '/inspectors/certificates-management',
      isReleased: true,
      linkLabel: 'Go to Inspector Cert.(s) Module',
    },

    isFeatureEnabled(FEATURES.DATA_MANAGEMENT) && {
      // Locations Management
      icon: <MapPinArea size={36} />,
      title: 'Manage Locations',
      tag: 'Data Management',
      description:
        'Manage cities: add or change entries. Handle city-based settings.',
      link: '/data-management/cities',
      isReleased: true,
      linkLabel: 'Go to Locations Module',
    },

    isFeatureEnabled(FEATURES.DATA_MANAGEMENT) && {
      // Exchange Rates Management
      icon: <CurrencyCircleDollar size={36} />,
      title: 'Manage Exchange Rates',
      tag: 'Data Management',
      description:
        'Manage exchange rates of different currencies against the USD.',
      link: '/data-management/exchange-rates',
      isReleased: true,
      linkLabel: 'Go to Exchange Rates Module',
    },

    isFeatureEnabled(FEATURES.STANDARDS_MASTER) && {
      // Standards Master
      icon: <Stack size={36} />,
      title: 'Standards Master',
      tag: 'Data Management',
      description:
        'Maintain the master list of standards used across the application.',
      link: '/data-management/standards-master',
      isReleased: true,
      linkLabel: 'Go to Standards Master',
    },

    isFeatureEnabled(FEATURES.VENDORS) && {
      // Vendor Management
      icon: <Warehouse size={36} />,
      title: 'Organize Vendors',
      tag: 'Vendor Management',
      description:
        'Manage vendors and their designated POCs for streamlined communication and coordination.',
      link: '/vendors',
      isReleased: true,
      linkLabel: 'Go to Vendors Module',
    },
  ];

  /**
   * Renders the main application introduction component.
   * This component displays a greeting message to the user,
   * along with a grid of navigation cards that represent different
   * modules the user can access. Each card includes an icon,
   * title, description, and a link to the respective module.
   * The cards are conditionally rendered based on their
   * `isReleased` status, ensuring that only available modules
   * are shown to the user.
   */
  return (
    <div className="min-h-[calc(100vh-60px)] w-full bg-transparent items-center flex px-10 py-5">
      <div className="mx-auto relative">
        <h1 className="text-3xl font-bold mb-3 text-brandSecondary">
          {getGreeting()},{' '}
          <span className="text-brandPrimary">
            {tokenManager.getFullName()}
          </span>
        </h1>
        {isNotFound ? (
          <WarningMessage className="mb-8 max-w-3xl">
            We couldn&apos;t find a module for your requested address. It may
            have been moved, renamed, or the link could be mistyped. But you can
            still access the following modules.
          </WarningMessage>
        ) : (
          <h2 className="text-2xl mb-8 text-gray-600">
            What would you like to do today?
          </h2>
        )}

        <div className="grid grid-cols-4 gap-6">
          {navigationCards.filter(Boolean).map((card, index) => (
            <div
              key={index}
              className="p-6 bg-white rounded-lg shadow-md border border-gray-100 flex flex-col hover:border-brandSecondary hover:shadow-lg transition-all duration-100"
            >
              <div className="flex-grow">
                <div className="flex justify-between items-start mb-4">
                  <div className="p-3 bg-slate-200 rounded-full text-brandSecondary w-fit">
                    {card.icon}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${tagColorMap[card.tag]}`}
                  >
                    {card.tag}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-brandPrimary">
                  {card.title}
                </h3>
                <p className="text-gray-600 text-md leading-relaxed mb-4">
                  {card.description}
                </p>
              </div>
              <div className="mt-auto">
                {card.isReleased ? (
                  <Link
                    to={card.link}
                    className="text-indigo-800 hover:text-brandPrimary text-sm font-medium hover:underline"
                  >
                    {card.linkLabel} →
                  </Link>
                ) : (
                  <span className="text-gray-500 text-sm font-medium">
                    Coming Shortly
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

AppIntro.displayName = 'AppIntro';

AppIntro.propTypes = {
  variant: PropTypes.oneOf(['default', 'notFound']),
};

export default AppIntro;
